import { Dialog } from "@headlessui/react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";

import usePublishResults from "../../data/usePublishResults";
import Modal from "../../layouts/Modal";
import {
  IMarketplacePublishOutcome,
  IPartPublishResult,
  Marketplace,
  MarketplaceListingStatus,
  PublishOutcome,
  marketplaceLabel,
  publishOutcomeLabel,
} from "../../models/Publishing";
import LoadingWheel from "../shared/LoadingWheel";

export interface PublishResultsModalProps {
  open: boolean;
  onClose: () => void;
  /** The correlation the publish endpoint returned. Null while nothing has been submitted. */
  correlationId: string | null;
  /** A result the caller already has (single publish returns one inline) - shown before the first poll. */
  initialParts?: IPartPublishResult[] | null;
  title?: string;
}

const externalUrl = (m: IMarketplacePublishOutcome) => {
  if (!m.externalId) return null;
  if (m.marketplace === Marketplace.Ebay) return `https://www.ebay.co.uk/itm/${m.externalId}`;
  return null;
};

// One pill per marketplace the part was sent to. Colour is the status; a Live eBay pill links out.
function MarketplaceStatusPill({ outcome }: { outcome: IMarketplacePublishOutcome }) {
  const label = marketplaceLabel[outcome.marketplace] ?? `Marketplace ${outcome.marketplace}`;
  const url = externalUrl(outcome);

  const styles: Record<number, string> = {
    [MarketplaceListingStatus.Live]: "bg-green-100 text-green-700",
    [MarketplaceListingStatus.Sold]: "bg-green-100 text-green-700",
    [MarketplaceListingStatus.Failed]: "bg-red-100 text-red-700",
    [MarketplaceListingStatus.Publishing]: "bg-amber-100 text-amber-700",
    [MarketplaceListingStatus.Withdrawn]: "bg-gray-100 text-gray-600",
    [MarketplaceListingStatus.NotListed]: "bg-gray-100 text-gray-500",
  };

  const icon =
    outcome.status === MarketplaceListingStatus.Publishing ? (
      <ArrowPathIcon className="h-3 w-3 animate-spin" aria-hidden="true" />
    ) : outcome.status === MarketplaceListingStatus.Failed ? (
      <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />
    ) : outcome.status === MarketplaceListingStatus.Live || outcome.status === MarketplaceListingStatus.Sold ? (
      <CheckCircleIcon className="h-3 w-3" aria-hidden="true" />
    ) : (
      <MinusCircleIcon className="h-3 w-3" aria-hidden="true" />
    );

  const className = `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
    styles[outcome.status] ?? "bg-gray-100 text-gray-500"
  }`;

  const body = (
    <>
      {icon}
      {label}
      {outcome.executedBySync && <span className="opacity-60" title="Published by Wilma Sync">·S</span>}
    </>
  );

  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={`${className} hover:underline`}>
        {body}
      </a>
    );
  }

  return <span className={className}>{body}</span>;
}

function OutcomeBadge({ outcome }: { outcome: PublishOutcome }) {
  const styles: Record<PublishOutcome, string> = {
    [PublishOutcome.Failed]: "bg-red-100 text-red-700",
    [PublishOutcome.Partial]: "bg-amber-100 text-amber-800",
    [PublishOutcome.Pending]: "bg-blue-100 text-blue-700",
    [PublishOutcome.Published]: "bg-green-100 text-green-700",
    [PublishOutcome.NoMarketplaces]: "bg-gray-100 text-gray-600",
  };

  return (
    <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${styles[outcome]}`}>
      {publishOutcomeLabel[outcome]}
    </span>
  );
}

// The failures a row needs to explain: the part-level error if it never reached a marketplace,
// otherwise each marketplace's own message.
function rowErrors(part: IPartPublishResult): string[] {
  if (part.error) return [part.error];

  return part.marketplaces
    .filter((m) => m.status === MarketplaceListingStatus.Failed && m.error)
    .map((m) => `${marketplaceLabel[m.marketplace]}: ${m.error}`);
}

function ResultRow({ part }: { part: IPartPublishResult }) {
  const errors = rowErrors(part);

  return (
    <tr className={part.outcome === PublishOutcome.Failed ? "bg-red-50/40" : undefined}>
      <td className="px-3 py-2 align-top">
        <OutcomeBadge outcome={part.outcome} />
      </td>
      <td className="px-3 py-2 align-top text-sm text-gray-700 whitespace-nowrap">{part.tagNumber || "—"}</td>
      <td className="px-3 py-2 align-top text-sm text-gray-900">
        <div className="line-clamp-2">{part.title || "—"}</div>
        {errors.length > 0 && (
          <ul className="mt-1 space-y-0.5 text-xs text-red-700">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        )}
        {part.alreadyApplied && errors.length === 0 && (
          <div className="mt-1 text-xs text-gray-500">Already published — nothing to do.</div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-wrap gap-1">
          {part.marketplaces.length === 0 ? (
            <span className="text-xs text-gray-500">—</span>
          ) : (
            [...part.marketplaces]
              .sort((a, b) => a.marketplace - b.marketplace)
              .map((m) => <MarketplaceStatusPill key={m.marketplace} outcome={m} />)
          )}
        </div>
      </td>
    </tr>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`rounded-md px-3 py-2 ${tone}`}>
      <div className="text-xl font-semibold leading-none">{value}</div>
      <div className="mt-1 text-xs uppercase tracking-wide opacity-80">{label}</div>
    </div>
  );
}

/**
 * The answer to "did that publish work?" per part, per marketplace. Opens as soon as the action is
 * submitted, shows pending rows while queued parts are still being worked, and polls until every
 * marketplace has answered. Failures sort to the top, then partial publishes, then fully published.
 */
export default function PublishResultsModal({
  open,
  onClose,
  correlationId,
  initialParts,
  title = "Publish Results",
}: PublishResultsModalProps) {
  const { response, results, isComplete, stoppedEarly } = usePublishResults(
    open ? correlationId : null,
    initialParts
  );

  const parts = response?.parts ?? [];
  const showInitialSpinner = !response && results.isLoading;

  return (
    <Modal open={open} setOpen={() => onClose()} width="sm:max-w-5xl">
      <div>
        <div className="flex items-center justify-between pr-8">
          <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
            {title}
          </Dialog.Title>
          {!isComplete && !stoppedEarly && response && (
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
              Waiting on {response.pending} part{response.pending === 1 ? "" : "s"}…
            </span>
          )}
          {isComplete && (
            <span className="inline-flex items-center gap-1.5 text-sm text-green-700">
              <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
              Complete
            </span>
          )}
        </div>

        {response && (
          <div className="mt-4 grid grid-cols-4 gap-2">
            <Stat label="Failed" value={response.failed} tone="bg-red-50 text-red-800" />
            <Stat label="Partial" value={response.partial} tone="bg-amber-50 text-amber-800" />
            <Stat label="Pending" value={response.pending} tone="bg-blue-50 text-blue-800" />
            <Stat label="Published" value={response.published} tone="bg-green-50 text-green-800" />
          </div>
        )}

        {stoppedEarly && (
          <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Still waiting on {response?.pending} part{response?.pending === 1 ? "" : "s"} after ten minutes.
            The listing queue may be behind — the Recently Listed table will show them when they land.
          </p>
        )}

        {results.isError && (
          <p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
            Couldn't load results: {String((results.error as Error)?.message ?? results.error)}
          </p>
        )}

        <div className="mt-4">
          {showInitialSpinner ? (
            <div className="py-10">
              <LoadingWheel />
            </div>
          ) : parts.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {response ? "Nothing was publishable in this batch." : "No results yet."}
            </p>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto rounded border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Outcome</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Tag</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Part</th>
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Marketplaces</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {parts.map((p) => (
                    <ResultRow key={`${p.partDataId}-${p.operationId ?? ""}`} part={p} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
